import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/types";
import { z } from "zod";

export type InvoicesAccessJSON = z.infer<typeof InvoicesAccessSchema.InvoicesAccessJSONZ>;

export type InvoicesAccessParams = z.infer<typeof InvoicesAccessSchema.InvoicesAccessZ>;

export default class InvoicesAccessSchema {
  public static readonly InvoicesAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.InvoicesAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly InvoicesAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.InvoicesAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): InvoicesAccessParams {
    return parseOrThrow(InvoicesAccessSchema.InvoicesAccessZ, values, {
      logError: true,
      schemaName: "InvoicesAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): InvoicesAccessJSON {
    return parseOrThrow(InvoicesAccessSchema.InvoicesAccessJSONZ, values, {
      logError: true,
      schemaName: "InvoicesAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is InvoicesAccessParams {
    try {
      InvoicesAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is InvoicesAccessJSON {
    try {
      InvoicesAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: InvoicesAccessJSON): InvoicesAccessParams {
    const parsed = parseOrThrow(InvoicesAccessSchema.InvoicesAccessJSONZ, data, {
      logError: true,
      schemaName: "InvoicesAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return InvoicesAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: InvoicesAccessParams): InvoicesAccessJSON {
    const parsed = InvoicesAccessSchema.validate(notification);
    return InvoicesAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    InvoicesAccessSchema.InvoicesAccessJSONZ,
    InvoicesAccessSchema.InvoicesAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.InvoicesAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.InvoicesAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
